import { useTranslation } from 'react-i18next'
import type { SettlementResultBlock } from '@services/settlements/types'

import { money, months, percent, quantity, unitPrice } from './format'

/** One cost block: its lines with how each amount came about, then a total. */
export function SettlementBlockTable({
  block
}: {
  block: SettlementResultBlock
}) {
  const { t } = useTranslation('settlement')

  if (block.lines.length === 0) return null

  return (
    <div className="rounded-2xl border border-ink/[0.08] p-4">
      <h3 className="m-0 mb-3 font-display text-base font-bold text-ink">
        {t(`blocks.${block.code}`, block.code)}
      </h3>

      <div className="space-y-1.5">
        {block.lines.map((line, index) => {
          const isAnnual = line.unit === 'eur_year'
          const amount = isAnnual
            ? null
            : quantity(line.quantity, line.unit)
          const price = unitPrice(line.unit_price)
          const share = line.unit_price || isAnnual ? null : percent(line.share)

          // An annual amount reads as the arithmetic the statement uses:
          // "405,86 € / 12 x 5,0 mes." rather than an opaque percentage.
          const annual = isAnnual
            ? t('result.annual_formula', {
                amount: unitPrice(line.quantity),
                months: months(line.share)
              })
            : null

          return (
            <div
              key={`${line.code}-${index}`}
              className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-sm"
            >
              <span className="text-ink">
                {t(`lines.${line.code}`, line.code)}
                {line.is_estimate && (
                  <span className="ml-2 rounded-full bg-peach/30 px-2 py-0.5 text-[11px] font-medium text-warning">
                    {t('usage.estimate')}
                  </span>
                )}
              </span>

              <span className="text-xs text-muted-foreground">
                {[annual, amount, price ? `× ${price} €` : null, share]
                  .filter(Boolean)
                  .join(' ')}
              </span>

              <span className="tabular-nums font-medium text-ink">
                {money(line.amount)}
              </span>
            </div>
          )
        })}
      </div>

      <div className="mt-3 flex items-baseline justify-between border-t border-ink/[0.08] pt-2 text-sm font-semibold text-ink">
        <span>{t('result.subtotal')}</span>
        <span className="tabular-nums">{money(block.total)}</span>
      </div>
    </div>
  )
}
